import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError, qs } from '../api/client';
import { useAuth } from '../state/auth';
import { Bandeau, Carte, Champ, Kpi, Modale, Squelette, Vide } from '../components/ui';
import { centimes, dateFr, libelleMouvement, moisLong, montant } from '../lib/format';
import { T } from '../i18n';
import type { BilanMois, Mouvement, Tiroir } from '../api/types';

interface Tiroirs { tiroirs: Tiroir[]; totalReferenceMinor: number; deviseReference: string }

const moisCourant = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const decale = (m: string, pas: number) => {
  const [a, n] = m.split('-').map(Number);
  const d = new Date(a!, n! - 1 + pas, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const moisFr = (m: string) => {
  const [a, n] = m.split('-').map(Number);
  return moisLong(new Date(a!, n! - 1, 1));
};

/**
 * La caisse des depenses.
 *
 * Une poche a part, pour l'eau, le cafe, le carburant du jour. Melangees aux
 * encaissements de location, ces sorties rendaient le solde du comptoir
 * illisible : on ne savait plus si la caisse etait juste.
 *
 * Hichem y pose un fonds — le « debut de caisse ». L'agent y puise et voit ce
 * qui lui reste. Ce qui est pose vient de la caisse principale, et la caisse
 * principale en garde la trace : rien n'apparait de nulle part.
 */
export default function Depenses() {
  const { peut } = useAuth();
  const [params, setParams] = useSearchParams();
  const mois = params.get('mois') ?? moisCourant();

  const [t, setT] = useState<Tiroirs | null>(null);
  const [bilan, setBilan] = useState<BilanMois | null>(null);
  const [mvts, setMvts] = useState<Mouvement[] | null>(null);
  const [ecran, setEcran] = useState<'' | 'fonds' | 'depense' | 'rendre'>('');
  /* D'ou vient l'argent pose : de la caisse principale, ou d'ailleurs.
   * Seul Hichem le sait, donc seul Hichem le dit. */
  const [source, setSource] = useState<'caisse' | 'exterieur'>('caisse');
  const [msg, setMsg] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);

  const charger = useCallback(() => {
    api.get<Tiroirs>('/caisse/depenses/tiroirs').then(setT).catch(() => {});
    api.get<Mouvement[]>(`/caisse/depenses/mouvements${qs({ mois })}`).then(setMvts).catch(() => {});
    api.get<BilanMois>(`/caisse/bilan-mensuel${qs({ mois, compte: 'depenses' })}`).then(setBilan).catch(() => {});
  }, [mois]);
  useEffect(charger, [charger]);

  const allerA = (m: string) => { params.set('mois', m); setParams(params, { replace: true }); };

  if (!t || !mvts || !bilan) return <Squelette lignes={6} />;

  const tx = T.depenses;
  const reste = t.totalReferenceMinor;
  /* Un fonds peut arriver de deux facons : transfere depuis la caisse
   * principale, ou apporte de l'exterieur. Le compteur additionne les deux,
   * sinon un apport exterieur ne serait compte nulle part. */
  const pose = bilan.transfertsEntrantsMinor + bilan.fondsMinor;

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{tx.titre}</h1>
          <p>{tx.sousTitre(t.deviseReference)}</p>
        </div>
        <div className="actions">
          <button className="btn btn-s" onClick={() => allerA(decale(mois, -1))}>‹</button>
          <span className="btn btn-s" style={{ pointerEvents: 'none', minWidth: 130, justifyContent: 'center' }}>
            {moisFr(mois)}
          </span>
          <button className="btn btn-s" onClick={() => allerA(decale(mois, 1))}>›</button>
          {peut('creer.mouvementCaisse') && (
            <button className="btn btn-p" onClick={() => setEcran('depense')}>{tx.enregistrerDepense}</button>
          )}
          {peut('modifier.finance') && (
            <>
              <button className="btn" onClick={() => setEcran('fonds')}>{tx.debutCaisse}</button>
              <button className="btn" onClick={() => setEcran('rendre')}>{tx.rendreCaisse}</button>
            </>
          )}
        </div>
      </div>

      {msg && <div style={{ marginBottom: 14 }}><Bandeau type="ok">{msg}</Bandeau></div>}
      {erreur && <div style={{ marginBottom: 14 }}><Bandeau type="ko">{erreur}</Bandeau></div>}

      {reste <= 0 && (
        <div style={{ marginBottom: 14 }}>
          <Bandeau type="warn" titre={tx.vide.titre}>
            {tx.vide.texte}
          </Bandeau>
        </div>
      )}

      <div className="grille g4" style={{ marginBottom: 16 }}>
        <Kpi libelle={tx.kpi.reste} valeur={montant(reste)} className="signature-kpi"
             detail={tx.kpi.resteDetail} />
        <Kpi libelle={tx.kpi.pose} valeur={montant(pose)}
             detail={bilan.fondsMinor > 0 && bilan.transfertsEntrantsMinor > 0
               ? tx.kpi.poseDontAilleurs(montant(bilan.fondsMinor))
               : bilan.fondsMinor > 0 ? tx.kpi.poseAilleurs : tx.kpi.poseCaisse} />
        <Kpi libelle={tx.kpi.depense} valeur={montant(bilan.depensesMinor)}
             detail={pose > 0
               ? tx.kpi.depenseConsomme(Math.round((bilan.depensesMinor / pose) * 100))
               : tx.kpi.depenseAucunFonds} />
        <Kpi libelle={tx.kpi.rendu} valeur={montant(bilan.transfertsSortantsMinor)}
             detail={tx.kpi.renduDetail} />
      </div>

      {bilan.parPersonne.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <Carte titre={tx.quiADepense} pad={false}>
            <div className="enveloppe-tbl">
              <table className="tbl">
                <thead><tr><th>{tx.colonnesPersonnes.personne}</th><th className="droite">{tx.colonnesPersonnes.operations}</th><th className="droite">{tx.colonnesPersonnes.depense}</th></tr></thead>
                <tbody>
                  {bilan.parPersonne.map((p) => (
                    <tr key={p.nom}>
                      <td><b>{p.nom}</b></td>
                      <td className="droite num">{p.nbOperations}</td>
                      <td className="droite montant" style={{ color: 'var(--danger)' }}>{montant(p.depensesMinor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Carte>
        </div>
      )}

      <Carte titre={tx.depensesDe(moisFr(mois))} pad={false}>
        {mvts.length === 0 ? (
          <div className="carte-p"><Vide titre={tx.aucunMouvement} /></div>
        ) : (
          <div className="enveloppe-tbl">
            <table className="tbl">
              <thead>
                <tr><th>{tx.colonnes.date}</th><th>{tx.colonnes.type}</th><th>{tx.colonnes.achat}</th><th>{tx.colonnes.par}</th><th className="droite">{tx.colonnes.montant}</th></tr>
              </thead>
              <tbody>
                {mvts.map((m) => (
                  <tr key={m._id}>
                    <td className="mini num">{dateFr(m.date)}</td>
                    <td className="mini sourdine">{libelleMouvement(m.type)}</td>
                    <td>
                      {m.libelle}
                      {m.pieceJustificative && <div className="mini sourdine">{tx.piece(m.pieceJustificative)}</div>}
                    </td>
                    <td className="mini">{(m.creePar as { nom?: string })?.nom ?? '—'}</td>
                    <td className="droite montant"
                        style={{ color: m.type === 'depense' ? 'var(--danger)' : 'var(--succes)' }}>
                      {m.type === 'depense' ? '−' : '+'}{montant(m.contrevaleurMinor)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Carte>

      <p className="mini sourdine" style={{ marginTop: 12, maxWidth: 820 }}>
        {tx.note}
      </p>

      {ecran === 'depense' && (
        <Saisie titre={tx.enregistrerDepense}
                aide={tx.saisieDepense.aide}
                bouton={tx.saisieDepense.bouton}
                devise={t.deviseReference}
                chemin="/caisse/depenses"
                onFini={(m) => { setEcran(''); setMsg(m); setErreur(null); charger(); }}
                onErreur={setErreur}
                onFermer={() => setEcran('')} />
      )}
      {ecran === 'fonds' && (
        <Saisie titre={tx.debutCaisse}
                aide={source === 'caisse' ? tx.saisieFonds.aideCaisse : tx.saisieFonds.aideAilleurs}
                bouton={tx.saisieFonds.bouton}
                devise={t.deviseReference}
                chemin="/caisse/depenses/fonds"
                corpsSupp={{ sens: 'vers_depenses', source }}
                libelleDefaut={tx.saisieFonds.libelleDefaut(moisFr(mois))}
                onFini={(m) => { setEcran(''); setMsg(m); setErreur(null); charger(); }}
                onErreur={setErreur}
                onFermer={() => setEcran('')}>
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--bord)' }}>
            <div className="micro" style={{ marginBottom: 8 }}>{tx.saisieFonds.dOu}</div>
            <div className="actions">
              <button type="button" className={`btn btn-s ${source === 'caisse' ? 'btn-p' : ''}`}
                      onClick={() => setSource('caisse')}>{tx.saisieFonds.deCaisse}</button>
              <button type="button" className={`btn btn-s ${source === 'exterieur' ? 'btn-p' : ''}`}
                      onClick={() => setSource('exterieur')}>{tx.saisieFonds.dAilleurs}</button>
            </div>
            <p className="mini sourdine" style={{ marginTop: 10, marginBottom: 0 }}>
              {source === 'caisse'
                ? tx.saisieFonds.noteCaisse
                : tx.saisieFonds.noteAilleurs}
            </p>
          </div>
        </Saisie>
      )}
      {ecran === 'rendre' && (
        <Saisie titre={tx.saisieRendre.titre}
                aide={tx.saisieRendre.aide}
                bouton={tx.saisieRendre.bouton}
                devise={t.deviseReference}
                chemin="/caisse/depenses/fonds"
                corpsSupp={{ sens: 'depenses_vers_caisse' }}
                libelleDefaut={tx.saisieRendre.libelleDefaut}
                onFini={(m) => { setEcran(''); setMsg(m); setErreur(null); charger(); }}
                onErreur={setErreur}
                onFermer={() => setEcran('')} />
      )}
    </>
  );
}

/** Un seul formulaire pour les trois gestes : ils ne different que par leur chemin. */
function Saisie({ titre, aide, bouton, devise, chemin, corpsSupp, libelleDefaut, onFini, onErreur, onFermer, children }: {
  titre: string; aide: string; bouton: string; devise: string; chemin: string;
  corpsSupp?: Record<string, string>; libelleDefaut?: string;
  onFini: (m: string) => void; onErreur: (e: string) => void; onFermer: () => void;
  children?: ReactNode;
}) {
  const [montantSaisi, setMontantSaisi] = useState('');
  const [libelle, setLibelle] = useState(libelleDefaut ?? '');
  const [piece, setPiece] = useState('');
  const [envoi, setEnvoi] = useState(false);

  async function envoyer() {
    setEnvoi(true);
    try {
      await api.post(chemin, {
        devise, montantMinor: centimes(Number(montantSaisi)),
        libelle: libelle.trim(),
        ...(piece.trim() ? { pieceJustificative: piece.trim() } : {}),
        ...corpsSupp,
      });
      onFini(T.depenses.saisie.enregistre(titre, montant(centimes(Number(montantSaisi)))));
    } catch (e) {
      onErreur(e instanceof ApiError ? e.erreur : T.depenses.saisie.echec);
      setEnvoi(false);
    }
  }

  return (
    <Modale titre={titre} onFermer={onFermer}>
      <p className="mini sourdine" style={{ marginBottom: 12 }}>{aide}</p>
      <Champ label={T.depenses.saisie.montant(devise)}>
        <input type="number" min={0} step={100} value={montantSaisi} autoFocus
               onChange={(e) => setMontantSaisi(e.target.value)} />
      </Champ>
      <div style={{ marginTop: 12 }}>
        <Champ label={T.depenses.saisie.libelle}>
          <input value={libelle} onChange={(e) => setLibelle(e.target.value)}
                 placeholder={T.depenses.saisie.libelleExemple} />
        </Champ>
      </div>
      {chemin === '/caisse/depenses' && (
        <div style={{ marginTop: 12 }}>
          <Champ label={T.depenses.saisie.piece} aide={T.depenses.saisie.pieceAide}>
            <input value={piece} onChange={(e) => setPiece(e.target.value)} />
          </Champ>
        </div>
      )}
      {children}
      <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
        <button className="btn" onClick={onFermer}>{T.commun.actions.annuler}</button>
        <button className="btn btn-p"
                disabled={envoi || !(Number(montantSaisi) > 0) || libelle.trim().length < 2}
                onClick={() => void envoyer()}>
          {bouton}
        </button>
      </div>
    </Modale>
  );
}
